import React from 'react'
import { Card, Typography, Space, Divider } from 'antd'
import { CodeOutlined } from '@ant-design/icons'

const { Title, Text, Paragraph } = Typography

const UsageExamples: React.FC = () => {
  return (
    <div style={{ padding: '20px' }}>
      <Title level={2}>
        <CodeOutlined style={{ marginRight: 8 }} />
        Contoh Penggunaan Sistem Sertifikat
      </Title>

      <Space direction="vertical" style={{ width: '100%' }}>
        {/* Basic Usage */}
        <Card title="1. Penggunaan Dasar" size="small">
          <Paragraph>
            <Text strong>Download sertifikat dengan posisi default:</Text>
          </Paragraph>
          <pre
            style={{ backgroundColor: '#f5f5f5', padding: 16, borderRadius: 8 }}
          >
            {`import { downloadCertificateWithPosition } from './utils/helpers'

const handleDownload = async () => {
  try {
    await downloadCertificateWithPosition({
      name: 'John Doe',
      x: 300,
      y: 400,
      fontSize: 24,
      fontColor: { r: 0, g: 0, b: 0 }
    })
    console.log('Sertifikat berhasil diunduh!')
  } catch (error) {
    console.error('Error:', error)
  }
}`}
          </pre>
        </Card>

        {/* Preview Usage */}
        <Card title="2. Preview Sertifikat" size="small">
          <Paragraph>
            <Text strong>Preview sertifikat di tab baru:</Text>
          </Paragraph>
          <pre
            style={{ backgroundColor: '#f5f5f5', padding: 16, borderRadius: 8 }}
          >
            {`import { previewCertificate } from './utils/certificateGenerator'

const handlePreview = async () => {
  try {
    await previewCertificate({
      name: 'John Doe',
      x: 300,
      y: 400,
      fontSize: 24,
      fontColor: { r: 0, g: 0, b: 0 }
    })
    console.log('Preview dibuka di tab baru!')
  } catch (error) {
    console.error('Error:', error)
  }
}`}
          </pre>
        </Card>

        {/* Custom Template */}
        <Card title="3. Template Kustom" size="small">
          <Paragraph>
            <Text strong>Gunakan template PDF yang berbeda:</Text>
          </Paragraph>
          <pre
            style={{ backgroundColor: '#f5f5f5', padding: 16, borderRadius: 8 }}
          >
            {`import { downloadGeneratedCertificate, CertificateTemplate } from './utils/certificateGenerator'

const customTemplate: CertificateTemplate = {
  templateUrl: '/assets/certificates/custom-template.pdf',
  namePosition: {
    x: 250,
    y: 350
  },
  defaultFontSize: 28,
  defaultFontColor: { r: 51, g: 51, b: 51 }
}

const handleDownload = async () => {
  try {
    await downloadGeneratedCertificate({
      name: 'John Doe',
      x: 250,
      y: 350,
      fontSize: 28,
      fontColor: { r: 51, g: 51, b: 51 }
    }, customTemplate)
    console.log('Sertifikat dengan template kustom berhasil diunduh!')
  } catch (error) {
    console.error('Error:', error)
  }
}`}
          </pre>
        </Card>

        {/* React Component */}
        <Card title="4. Komponen React" size="small">
          <Paragraph>
            <Text strong>Gunakan komponen editor posisi:</Text>
          </Paragraph>
          <pre
            style={{ backgroundColor: '#f5f5f5', padding: 16, borderRadius: 8 }}
          >
            {`import CertificatePositionEditor from './CertificatePositionEditor'

const CertificatePage = () => {
  const handleSave = (config) => {
    console.log('Konfigurasi tersimpan:', config)
    // Simpan ke database atau lakukan aksi lain
  }

  return (
    <CertificatePositionEditor
      studentName="John Doe"
      onSave={handleSave}
    />
  )
}`}
          </pre>
        </Card>

        {/* Error Handling */}
        <Card title="5. Error Handling" size="small">
          <Paragraph>
            <Text strong>Tangani error dengan baik:</Text>
          </Paragraph>
          <pre
            style={{ backgroundColor: '#f5f5f5', padding: 16, borderRadius: 8 }}
          >
            {`const handleDownload = async () => {
  try {
    setLoading(true)
    await downloadCertificateWithPosition({
      name: 'John Doe',
      x: 300,
      y: 400,
      fontSize: 24,
      fontColor: { r: 0, g: 0, b: 0 }
    })
    message.success('Sertifikat berhasil diunduh!')
  } catch (error) {
    if (error.message.includes('Failed to fetch')) {
      message.error('Template sertifikat tidak ditemukan')
    } else if (error.message.includes('PDF')) {
      message.error('Error dalam memproses PDF')
    } else {
      message.error('Terjadi kesalahan yang tidak diketahui')
    }
    console.error('Error details:', error)
  } finally {
    setLoading(false)
  }
}`}
          </pre>
        </Card>

        {/* Batch Processing */}
        <Card title="6. Batch Processing" size="small">
          <Paragraph>
            <Text strong>Generate sertifikat untuk banyak siswa:</Text>
          </Paragraph>
          <pre
            style={{ backgroundColor: '#f5f5f5', padding: 16, borderRadius: 8 }}
          >
            {`const students = [
  'John Doe',
  'Jane Smith',
  'Bob Johnson',
  'Alice Brown'
]

const generateBatchCertificates = async () => {
  const results = []
  
  for (const student of students) {
    try {
      await downloadGeneratedCertificate({
        name: student,
        x: 300,
        y: 400,
        fontSize: 24,
        fontColor: { r: 0, g: 0, b: 0 }
      })
      results.push({ student, status: 'success' })
    } catch (error) {
      results.push({ student, status: 'error', error: error.message })
    }
  }
  
  console.log('Batch results:', results)
}`}
          </pre>
        </Card>
      </Space>

      <Divider />

      <Card title="Tips Penggunaan">
        <ul>
          <li>
            <Text strong>Koordinat PDF:</Text> Gunakan koordinat 0-595pt untuk X
            dan 0-842pt untuk Y
          </li>
          <li>
            <Text strong>Font Size:</Text> Rekomendasi ukuran font 16-48pt untuk
            keterbacaan yang baik
          </li>
          <li>
            <Text strong>Template:</Text> Pastikan file template PDF ada dan
            dapat diakses
          </li>
          <li>
            <Text strong>Error Handling:</Text> Selalu gunakan try-catch untuk
            menangani error
          </li>
          <li>
            <Text strong>Loading States:</Text> Tampilkan loading state saat
            memproses sertifikat
          </li>
          <li>
            <Text strong>Preview:</Text> Gunakan preview sebelum download untuk
            memastikan hasil yang diinginkan
          </li>
        </ul>
      </Card>
    </div>
  )
}

export default UsageExamples
